import { VariableStateMap } from '@/core';

/**
 * Streaming 53-bit non-cryptographic string hash (cyrb53). Feeds input
 * incrementally via `update(str)` and finalizes with `digest()`, avoiding
 * large intermediate string allocations for multi-select variables with
 * many values.
 */
export class Cyrb53Hasher {
  private h1 = 0xdeadbeef;
  private h2 = 0x41c6ce57;

  update(str: string): void {
    for (let i = 0; i < str.length; i++) {
      const ch = str.charCodeAt(i);
      this.h1 = Math.imul(this.h1 ^ ch, 2654435761);
      this.h2 = Math.imul(this.h2 ^ ch, 1597334677);
    }
  }

  digest(): string {
    let { h1, h2 } = this;
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
  }
}

/**
 * Maximum total character count for the JSON-stringified key before we
 * switch to a Cyrb53 hash digest. Keys at or below this size are returned
 * verbatim (leveraging V8's fast native JSON.stringify, easy to debug);
 * larger keys are hashed to bound retained memory in TanStack Query caches.
 */
export const RAW_VARIABLE_KEY_MAX_CHARS = 512;

/**
 * Returns a string representing the current variable values, used as a
 * cache-key discriminator in TanStack Query keys. Uses a hybrid strategy:
 * JSON.stringify each variable value (collision-safe because JSON naturally
 * quotes and escapes), then return the joined result verbatim if it's small
 * enough, or fold it into a Cyrb53 hash digest if it exceeds
 * `RAW_VARIABLE_KEY_MAX_CHARS`.
 *
 * Pass `useHashing = false` to always return the raw JSON key regardless
 * of size (used as a kill switch via the `disableVariableStateKeyHashing`
 * feature flag).
 */
export function getVariableValuesKey(v: VariableStateMap, useHashing = true): string {
  const parts = Object.values(v).map((s) => JSON.stringify(s.value));
  const raw = parts.join(',');

  if (!useHashing || raw.length <= RAW_VARIABLE_KEY_MAX_CHARS) {
    return raw;
  }

  const hasher = new Cyrb53Hasher();
  for (const part of parts) hasher.update(part);
  return hasher.digest();
}

/**
 * Filter the variable state map to only include the variables specified in `names`.
 */
export function filterVariableStateMap(v: VariableStateMap, names?: string[]) {
  if (!names) {
    return v;
  }
  return Object.fromEntries(Object.entries(v).filter(([name]) => names.includes(name)));
}
